import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, ArrowLeft } from 'lucide-react';
import SEO from '../components/SEO';
import RelatedPages from '../components/RelatedPages';
import { relatedPagesMap } from '../data/relatedPages';

const ONEDOC_URL = "https://www.onedoc.ch/fr/therapeute-en-nutrition-mco/vevey/pcyv4/zara-nawid";

const PrestationConsultation = () => {
  return (
    <div style={{ marginTop: '100px', background: 'var(--bg-page)' }}>
      <SEO
        title="Consultation nutritionnelle — 150 CHF | Nutri Centre Riviera Vevey & Clarens"
        description="Première étape de votre parcours nutritionnel (~1h00) : recueil de vos habitudes, analyse corporelle par bioimpédance, installation Nutrium, rééquilibrage personnalisé. À Vevey, Clarens et Montreux. 150 CHF — remboursé ASCA / RME."
        keywords="consultation nutritionnelle Vevey, première consultation nutrition Clarens, bilan nutritionnel Montreux, bioimpédance, Nutrium, rééquilibrage alimentaire"
        url="https://nutricentreriviera.ch/prestations/consultation-nutritionnelle"
      />

      <section
        data-testid="prestation-consultation-section"
        style={{
          padding: '4rem 0 6rem',
          minHeight: '70vh'
        }}
      >
        <div className="container" style={{ maxWidth: '900px' }}>
          {/* Back link */}
          <Link
            to="/"
            data-testid="prestation-back-link"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              color: 'var(--text-muted)',
              textDecoration: 'none',
              marginBottom: '3rem',
              fontSize: '0.95rem',
              transition: 'color 0.2s ease'
            }}
            onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--accent-primary)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--text-muted)'; }}
          >
            <ArrowLeft size={18} />
            Retour aux prestations
          </Link>

          {/* Title */}
          <h1
            data-testid="prestation-title"
            style={{
              fontFamily: '"Playfair Display", Georgia, serif',
              fontSize: 'clamp(2.5rem, 5vw, 3.75rem)',
              fontWeight: 700,
              lineHeight: 1.15,
              color: 'var(--text-primary)',
              textAlign: 'center',
              marginBottom: '3rem',
              letterSpacing: '-0.01em'
            }}
          >
            Consultation nutritionnelle
          </h1>

          {/* Price Badge */}
          <div style={{ textAlign: 'center', marginBottom: '3.5rem' }}>
            <span
              data-testid="prestation-price-badge"
              style={{
                display: 'inline-flex',
                alignItems: 'baseline',
                gap: '0.4rem',
                background: 'var(--accent-primary-light, #E9EEE8)',
                color: 'var(--accent-primary)',
                padding: '1.1rem 3rem',
                borderRadius: '999px',
                fontSize: '1.5rem',
                fontWeight: 700,
                fontFamily: 'Inter, system-ui, sans-serif'
              }}
            >
              150 CHF
              <span style={{ fontSize: '1.2rem' }}>*</span>
            </span>
          </div>

          {/* Body intro */}
          <p
            data-testid="prestation-intro"
            style={{
              fontSize: '1.15rem',
              lineHeight: 1.75,
              color: 'var(--text-primary)',
              marginBottom: '2rem',
              maxWidth: '780px',
              margin: '0 auto 2rem'
            }}
          >
            Cette consultation dure environ <strong>1h00</strong>, il s'agit de la première étape de votre parcours.
            Elle est composée de la manière suivante :
          </p>

          {/* Bullet list */}
          <ul
            data-testid="prestation-bullets"
            style={{
              listStyle: 'none',
              padding: 0,
              margin: '0 auto 3.5rem',
              maxWidth: '780px',
              display: 'flex',
              flexDirection: 'column',
              gap: '1.25rem'
            }}
          >
            {[
              <>Prise de contact et recueil d'informations telles que vos habitudes et comportements alimentaires, contexte personnel, médical etc.</>,
              <>Prise des différentes mensurations par <strong>bioimpédance</strong> : analyse corporelle complète et mensurations (tour des hanches, tour de cuisse, etc.)</>,
              <>Installation de l'application <strong>« Nutrium »</strong> pour le suivi nutritionnel</>,
              <>Mise en place d'un <strong>rééquilibrage alimentaire personnalisé</strong></>
            ].map((item, i) => (
              <li
                key={i}
                style={{
                  display: 'flex',
                  gap: '1rem',
                  fontSize: '1.08rem',
                  lineHeight: 1.65,
                  color: 'var(--text-primary)',
                  alignItems: 'flex-start'
                }}
              >
                <span
                  style={{
                    width: '8px',
                    height: '8px',
                    minWidth: '8px',
                    borderRadius: '50%',
                    background: 'var(--accent-primary)',
                    marginTop: '0.7rem',
                    flexShrink: 0
                  }}
                />
                <span>{item}</span>
              </li>
            ))}
          </ul>

          {/* CTA Button */}
          <div style={{ maxWidth: '780px', margin: '0 auto' }}>
            <a
              href={ONEDOC_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="prestation-cta"
              style={{
                background: 'var(--accent-primary-light, #E9EEE8)',
                color: 'var(--accent-primary)',
                width: '100%',
                padding: '1.4rem 2rem',
                borderRadius: '999px',
                textDecoration: 'none',
                fontWeight: 700,
                fontSize: '1.1rem',
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '1rem',
                transition: 'all 0.3s ease',
                border: '2px solid transparent'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'var(--accent-primary)';
                e.currentTarget.style.color = 'white';
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 10px 28px rgba(61, 84, 70, 0.25)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'var(--accent-primary-light, #E9EEE8)';
                e.currentTarget.style.color = 'var(--accent-primary)';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              Réserver maintenant
              <ArrowUpRight size={22} />
            </a>

            <p
              data-testid="prestation-reimbursement"
              style={{
                textAlign: 'right',
                marginTop: '1.5rem',
                fontStyle: 'italic',
                fontSize: '1rem',
                color: 'var(--text-muted)'
              }}
            >
              * remboursé par l'assurance ASCA ou RME
            </p>
          </div>
        </div>
      </section>

      {/* SECTION 2 — Consultation de suivi */}
      <section
        data-testid="prestation-suivi-section"
        style={{
          padding: '5rem 0',
          background: 'white',
          borderTop: '1px solid var(--border-light)'
        }}
      >
        <div className="container" style={{ maxWidth: '900px' }}>
          <h2
            style={{
              fontFamily: '"Playfair Display", Georgia, serif',
              fontSize: 'clamp(2rem, 4vw, 2.75rem)',
              fontWeight: 700,
              lineHeight: 1.2,
              color: 'var(--text-primary)',
              textAlign: 'center',
              marginBottom: '2.5rem',
              letterSpacing: '-0.01em'
            }}
          >
            Consultation de suivi
          </h2>

          <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <span style={{
              display: 'inline-flex',
              alignItems: 'baseline',
              gap: '0.4rem',
              background: 'var(--accent-primary-light, #E9EEE8)',
              color: 'var(--accent-primary)',
              padding: '1rem 2.75rem',
              borderRadius: '999px',
              fontSize: '1.4rem',
              fontWeight: 700
            }}>
              100 CHF
              <span style={{ fontSize: '1.1rem' }}>*</span>
            </span>
          </div>

          <div style={{ maxWidth: '780px', margin: '0 auto', textAlign: 'justify' }}>
            <p style={{
              fontSize: '1.1rem',
              lineHeight: 1.8,
              color: 'var(--text-primary)',
              marginBottom: '1.5rem'
            }}>
              Cette consultation de suivi dure environ <strong>1h00</strong> et elle est planifiée généralement <strong>3 semaines après la première consultation</strong>. Cette séance permet d'évaluer la progression de vos objectifs. Durant cette consultation, nous procéderons à un bilan approfondi, incluant une analyse corporelle complète, afin de prendre en compte les avancées réalisées et d'identifier d'éventuelles difficultés rencontrées.
            </p>
            <p style={{
              fontSize: '1.1rem',
              lineHeight: 1.8,
              color: 'var(--text-primary)',
              marginBottom: '2.5rem'
            }}>
              Cet examen détaillé nous permettra d'ajuster notre approche et de personnaliser davantage votre programme nutritionnel en fonction de vos besoins spécifiques. La fréquence des consultations de suivi est généralement prévue <strong>une fois par mois</strong>.
            </p>

            {/* CTA */}
            <a
              href={ONEDOC_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="prestation-suivi-cta"
              style={{
                background: 'var(--accent-primary-light, #E9EEE8)',
                color: 'var(--accent-primary)',
                width: '100%',
                padding: '1.3rem 2rem',
                borderRadius: '999px',
                textDecoration: 'none',
                fontWeight: 700,
                fontSize: '1.05rem',
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '1rem',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'var(--accent-primary)';
                e.currentTarget.style.color = 'white';
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 10px 28px rgba(61, 84, 70, 0.25)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'var(--accent-primary-light, #E9EEE8)';
                e.currentTarget.style.color = 'var(--accent-primary)';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              Réserver maintenant
              <ArrowUpRight size={22} />
            </a>

            <p style={{
              textAlign: 'right',
              marginTop: '1.25rem',
              fontStyle: 'italic',
              fontSize: '0.95rem',
              color: 'var(--text-muted)'
            }}>
              * remboursé par l'assurance ASCA ou RME
            </p>
          </div>
        </div>
      </section>

      {/* SECTION 3 — Analyse So/check */}
      <section
        data-testid="prestation-socheck-section"
        style={{
          padding: '5rem 0',
          background: 'var(--bg-page)'
        }}
      >
        <div className="container" style={{ maxWidth: '1180px' }}>
          <h2
            style={{
              fontFamily: '"Playfair Display", Georgia, serif',
              fontSize: 'clamp(2rem, 4vw, 2.75rem)',
              fontWeight: 700,
              lineHeight: 1.2,
              color: 'var(--text-primary)',
              marginBottom: '3rem',
              letterSpacing: '-0.01em'
            }}
          >
            Analyse So/check
          </h2>

          <div
            className="socheck-grid"
            style={{
              display: 'grid',
              gridTemplateColumns: '1.2fr 1fr',
              gap: '4rem',
              alignItems: 'start'
            }}
          >
            {/* Texte gauche */}
            <div>
              <p style={{
                fontSize: '1.08rem',
                lineHeight: 1.85,
                color: 'var(--text-primary)',
                marginBottom: '1.5rem',
                textAlign: 'justify'
              }}>
                Le bilan <strong>OligoCheck</strong> représente une analyse approfondie évaluant instantanément vos niveaux de <strong>minéraux, vitamines, oligo-éléments, stress oxydatif et métaux lourds</strong> par le biais de la spectrophotométrie. Cette méthode vous offre l'avantage d'un bilan rapide et indolore, tout en fournissant des informations cruciales sur votre santé nutritionnelle.
              </p>
              <p style={{
                fontSize: '1.08rem',
                lineHeight: 1.85,
                color: 'var(--text-primary)',
                marginBottom: '2rem',
                textAlign: 'justify'
              }}>
                Les résultats de cette évaluation sont une <strong>base solide pour personnaliser des conseils adaptés</strong> à votre situation, que ce soit pour des traitements spécifiques ou pour des compléments alimentaires.
              </p>

              {/* CTA */}
              <a
                href={ONEDOC_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="prestation-socheck-cta"
                style={{
                  background: 'var(--accent-primary-light, #E9EEE8)',
                  color: 'var(--accent-primary)',
                  width: '100%',
                  padding: '1.3rem 2rem',
                  borderRadius: '999px',
                  textDecoration: 'none',
                  fontWeight: 700,
                  fontSize: '1.05rem',
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '1rem',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'var(--accent-primary)';
                  e.currentTarget.style.color = 'white';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 10px 28px rgba(61, 84, 70, 0.25)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'var(--accent-primary-light, #E9EEE8)';
                  e.currentTarget.style.color = 'var(--accent-primary)';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                Réserver maintenant
                <ArrowUpRight size={22} />
              </a>

              <p style={{
                textAlign: 'center',
                marginTop: '1.25rem',
                fontStyle: 'italic',
                fontSize: '0.95rem',
                color: 'var(--text-muted)'
              }}>
                * remboursé par l'assurance ASCA ou RME
              </p>
            </div>

            {/* Image droite */}
            <div style={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'center'
            }}>
              <img
                src="/images/consultation-cabinet.webp"
                alt="Bilan OligoCheck — analyse complète des minéraux, vitamines, oligo-éléments et stress oxydatif"
                loading="lazy"
                decoding="async"
                style={{
                  maxWidth: '100%',
                  height: 'auto',
                  filter: 'drop-shadow(0 12px 32px rgba(0,0,0,0.08))'
                }}
              />
            </div>
          </div>

          <style>{`
            @media (max-width: 968px) {
              .socheck-grid {
                grid-template-columns: 1fr !important;
                gap: 2.5rem !important;
              }
            }
          `}</style>
        </div>
      </section>

      <RelatedPages items={relatedPagesMap['home']} />
    </div>
  );
};

export default PrestationConsultation;
